import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Typography
} from "@mui/material";
import {
  Area, AreaChart, Bar, CartesianGrid, ComposedChart,
  LabelList, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  get24hrPay,
  calc24hrPayForDate,
  save24hrPay,
  delete24hrPay,
  getDashboardPtpSummary as getPtpSummary,
  getDashboardPtpByDay   as getPtpByDay,
  // Funnel money steps — see paymentsTotals below.
  getDashboardPaymentsSummary as getPaymentsSummary,
  getDashboardAccountsHourly as getAccounts,
} from "../../services/pbi/pbiservices";

// ── Constants ──────────────────────────────────────────────────────
const BLUE  = "#378ADD";   // amount (primary / hero metric)
const GREEN = "#1D9E75";   // count
const PINK  = "#D4537E";
const AMBER = "#BA7517";    // percentage
// Dotted average rule and the figure beside each chart title. A hue no series
// uses, so the annotation never reads as another measurement.
const AVG_INK  = "#0F6E56";

// ── ONE COLOUR PER METRIC ──────────────────────────────────────────
// A metric reads the same hue wherever it appears: its funnel bar, the figure
// on its KPI tile, its line, its area, its bar, its table column.
//
// Before this the same funnel had RPC and Kept both green and Sent and Kept
// both blue, and the same metric took a different hue on each page — Kept was
// green here, blue on Payments, deep blue on the 24-hr page. Colour was
// decoration; now it identifies.
//
// Chosen for separation rather than family. Kept is a component of PTP and
// Uncommitted is the rest of it, but shading them as siblings of one hue
// makes them hard to tell apart in a legend, which is the one place they are
// read side by side.
const C_SENT        = "#0C447C";  // navy
const C_ACCOUNTS    = "#378ADD";  // blue
const C_RPC         = "#1D9E75";  // green
const C_PTP         = "#BA7517";  // amber
const C_KEPT        = "#7F77DD";  // purple
const C_SL          = "#D4537E";  // pink
const C_UNCOMMITTED = "#3DBE92";  // mint
const C_24HR        = "#A01F50";  // deep pink


const GRAN_OPTIONS  = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

// ── Date helpers (lifted from PaymentsDashboard so the page is standalone) ──
function toLocal(d) {
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${dy}`;
}

function getDateRange(preset) {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":      start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":      start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":      start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":     start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":     start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":    start = thisMonday;             end = now; break;
    case "CW+Last": start = lastMonday;             end = now; break;
    case "This Mo": start = new Date(y, m, 1);      end = now; break;
    case "Cur+Last":start = new Date(y, m - 1, 1);  end = now; break;
    case "Prev 2Mo":start = new Date(y, m - 2, 1);  end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:        start = new Date(y, m - 3, 1);  end = new Date(y, m, 0); break;
  }
  return { startDate: toLocal(start), endDate: toLocal(end) };
}

// Previous period = same duration, ending the day before current starts.
function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const mm = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return mm ? new Date(+mm[1], +mm[2] - 1, +mm[3]) : new Date(s);
  };
  const start = parse(startDate);
  const end   = parse(endDate);
  const durMs = end.getTime() - start.getTime();
  const dayMs = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Format / misc helpers ──────────────────────────────────────────
function fmtCurrency(n) {
  const num = Number(n);
  if (!n || isNaN(num)) return "$0.00";
  // Switch to M only once the K form would need 4 digits — i.e. once
  // it rounds to 1000.0K or more.
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(2)}`;
}

// Compact currency for the funnel — 1 decimal on K (matches PtpDashboard).
function fmtCurrencyK1(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  // Switch to M only once the K form would need 4 digits — i.e. once
  // it rounds to 1000.0K or more.
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

// Graph currency — 1 decimal everywhere ($1.2K / $45.5). Used for chart
// axis ticks, tooltips, and inline value labels so the graphs stay
// readable without the extra digit. KPI tiles keep full 2-decimal
// fmtCurrency for precise headline numbers.
function fmtCurrencyG(n) {
  const num = Number(n);
  if (!n || isNaN(num)) return "$0.0";
  // Switch to M only once the K form would need 4 digits — i.e. once
  // it rounds to 1000.0K or more.
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(1)}`;
}

function fmtPct(v) {
  const n = Number(v);
  return isNaN(n) ? "—" : `${n.toFixed(2)}%`;
}

function fmtDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  const d = new Date(s.replace(/-/g, "/"));
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ── AVERAGE RULE + HEADER FIGURE ───────────────────────────────────
// The two rules the scorecard uses, for the same reasons.
//
// activeMean — mean over buckets WITH ACTIVITY only. A day the system was not
// running is not a zero; counting it drags the rule down and makes every real
// day look above trend.
//
// pooledPct — for a RATE, total numerator over total denominator. NOT the mean
// of the per-bucket percentages, which weights a 40-send day the same as a
// 4,000-send one; on a book whose volume moves the two differ by multiples.
function activeMean(rows, key) {
  const active = (rows || []).filter(r => safeNum(r[key]) > 0);
  return active.length
    ? active.reduce((a, r) => a + safeNum(r[key]), 0) / active.length
    : 0;
}

function pooledPct(rows, numKey, denKey) {
  const den = (rows || []).reduce((a, r) => a + safeNum(r[denKey]), 0);
  return den
    ? ((rows || []).reduce((a, r) => a + safeNum(r[numKey]), 0) * 100) / den
    : 0;
}

function median(sorted) {
  if (!sorted.length) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function modeRounded(values) {
  if (!values.length) return 0;
  const counts = new Map();
  let bestVal = values[0], bestCnt = 0;
  for (const v of values) {
    const k = Math.round(v);
    const c = (counts.get(k) || 0) + 1;
    counts.set(k, c);
    if (c > bestCnt) { bestCnt = c; bestVal = k; }
  }
  return bestVal;
}

// up:   current increased vs prev → ▲/▼ ; good: favorable → green/red
function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur), p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return { up, good: goodWhenUp ? up : !up, text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}` };
}

// ── FUNNEL MONEY STEPS COME FROM THE PAYMENTS ROUTE ────────────────
// PTP, Kept and Smart Links are read from /dashboard/payments-summary, which
// aggregates raw customerconvevent, rather than from summary_ptp_hourly.
//
// The two disagree. The summary table is built by a refresh proc and can lag
// or drift from the events it was built from, and the Payments dashboard
// already reports the raw figures — so a funnel sourced from the summary
// showed a different PTP than the page next to it for the same range. One of
// them had to win, and the one closer to the events is the one to trust.
//
// Sent, Accounts and RPC have no counterpart in that route and still come
// from their own sources.
//
// Falls back to the summary when the payments call returns nothing, so a
// failed fetch degrades to the previous numbers rather than an empty funnel.
function paymentsTotals(rows) {
  const t = (rows || []).reduce((a, r) => ({
    kept:       a.kept       + safeNum(r.payment_count),
    keptAmt:    a.keptAmt    + safeNum(r.payment_total),
    promise:    a.promise    + safeNum(r.promise_count),
    promiseAmt: a.promiseAmt + safeNum(r.promise_total),
    sl:         a.sl         + safeNum(r.smartlink_count),
    slAmt:      a.slAmt      + safeNum(r.smartlink_total),
  }), { kept: 0, keptAmt: 0, promise: 0, promiseAmt: 0, sl: 0, slAmt: 0 });
  return {
    ...t,
    ptp:    t.kept + t.promise,
    ptpAmt: t.keptAmt + t.promiseAmt,
    has:    (rows || []).length > 0,
  };
}

// ── Data model ─────────────────────────────────────────────────────
// A "row" is one day of 24-hr payments: { date: "YYYY-MM-DD", count, amount }.
// Front-end only for now — rows are entered in the modal (manually or via
// the IDs calculator) and held in local state. No load endpoint yet.

// Keep only rows whose date falls in [start, end] (ISO strings compare lexically).
function rowsInRange(rows, startDate, endDate) {
  return (rows || []).filter(r => {
    const d = String(r.date).slice(0, 10);
    return d >= startDate && d <= endDate;
  });
}

// Bucket daily rows by the chosen granularity for charting.
function bucketKey(dateStr, gran) {
  const s = String(dateStr).slice(0, 10);
  if (gran === "Month") return s.slice(0, 7);              // YYYY-MM
  if (gran === "Week") {
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));     // Monday of that week
    return toLocal(dt);
  }
  return s;                                                 // Day
}
function bucketLabel(key, gran) {
  if (gran === "Month") {
    const [y, m] = key.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  }
  // Day / Week buckets: two-char weekday + MM/DD, no year (Tu 06/16).
  const [y, m, d] = String(key).slice(0, 10).split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (isNaN(dt)) return fmtDate(key);
  const wd = ["Su","Mo","Tu","We","Th","Fr","Sa"][dt.getDay()];                 // Tu
  const md = dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" }); // 06/16
  return `${wd} ${md}`;
}
function bucketize(rows, gran) {
  const map = new Map();
  for (const r of rows) {
    const k = bucketKey(r.date, gran);
    const e = map.get(k) || { count: 0, amount: 0 };
    e.count  += safeNum(r.count);
    e.amount += safeNum(r.amount);
    map.set(k, e);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([k, v]) => ({ date: bucketLabel(k, gran), count: v.count, amount: v.amount }));
}

// Distribution stats — always computed on the daily rows (matches the
// "(daily)" basis used on PaymentsDashboard). Max/Min are per-day totals.
function aggregate(rows) {
  const list       = rows || [];
  const totalCount = list.reduce((a, b) => a + safeNum(b.count),  0);
  const totalAmt   = list.reduce((a, b) => a + safeNum(b.amount), 0);

  const dayAmts   = list.map(r => safeNum(r.amount)).filter(v => v > 0);
  const dayCounts = list.map(r => safeNum(r.count)).filter(v => v > 0);

  // per-day average $ per payment, for active days only
  const perDayAvg = list
    .filter(r => safeNum(r.count) > 0)
    .map(r => safeNum(r.amount) / safeNum(r.count))
    .filter(v => v > 0);
  const activeDays = perDayAvg.length;
  const sortedAvg  = [...perDayAvg].sort((a, b) => a - b);

  return {
    totalCount, totalAmt,
    avgAmt:        totalCount > 0 ? totalAmt / totalCount : 0,   // avg $ per payment
    avgCountPerDay:activeDays  > 0 ? totalCount / activeDays : 0,
    maxDayAmt:     dayAmts.length   ? Math.max(...dayAmts)   : 0,
    minDayAmt:     dayAmts.length   ? Math.min(...dayAmts)   : 0,
    maxDayCount:   dayCounts.length ? Math.max(...dayCounts) : 0,
    minDayCount:   dayCounts.length ? Math.min(...dayCounts) : 0,
    medianDailyAvg:median(sortedAvg),
    modeDailyAvg:  modeRounded(perDayAvg),
  };
}

// ── ID parsing + calculate stub ────────────────────────────────────
// Tolerates commas, spaces, tabs, newlines. Dedupes, drops empties.
function parseIds(text) {
  return [...new Set(
    String(text || "")
      .split(/[\s,;]+/)
      .map(s => s.trim())
      .filter(Boolean)
  )];
}

// Per-date calc + load/save now come from the API via pbiservices:
//   calc24hrPayForDate({ date, ids }) → { date, count, amount, requested, matched }
//   get24hrPay(params)                → [{ pay_date, payment_count, payment_amount, ... }]
//   save24hrPay(rows) / delete24hrPay(date)

// ── Sub-components (mirrors PaymentsDashboard) ─────────────────────
function KpiTile({ label, count, amount, percent, color = BLUE, delta }) {
  const hasPercent = percent !== null && percent !== undefined;
  const hasCount   = count   !== null && count   !== undefined;
  const hasAmount  = amount  !== null && amount  !== undefined;

  let heroValue, heroIsAmount = false;
  if      (hasPercent) heroValue = percent;
  else if (hasCount)   heroValue = count;
  else if (hasAmount)  { heroValue = amount; heroIsAmount = true; }
  else                 heroValue = "—";

  const heroColor       = (hasPercent || hasCount || hasAmount) ? color : "text.disabled";
  const showAmountRow   = hasAmount && !heroIsAmount;
  const showFooterCount = hasCount && hasPercent;

  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 12px", minWidth: 0,
      display: "flex", flexDirection: "column", gap: "6px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.05em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{label}</Typography>

      <Box sx={{ minHeight: 26 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 500, color: heroColor, lineHeight: 1.2 }}>
          {heroValue}
        </Typography>
      </Box>

      <Box sx={{ minHeight: 22 }}>
        {showAmountRow ? (
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: "text.primary", lineHeight: 1.2 }}>{amount}</Typography>
        ) : (
          <Typography sx={{ fontSize: 15, color: "text.disabled", lineHeight: 1.2 }}>—</Typography>
        )}
      </Box>

      <Box sx={{ minHeight: 18 }}>
        {showFooterCount ? (
          <Typography sx={{ fontSize: 12, fontWeight: 500, color, lineHeight: 1.2 }}>{count}</Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled", lineHeight: 1.2 }}>—</Typography>
        )}
      </Box>

      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.25 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>{children}</Typography>
  );
}

function MiniStat({ label, value, color = BLUE }) {
  return (
    <Box sx={{ background: "var(--mui-palette-background-default, #f7f7f7)", borderRadius: 2, p: "8px 12px", minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 16, fontWeight: 500, color, lineHeight: 1.2,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{value}</Typography>
    </Box>
  );
}

function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{ background: "var(--mui-palette-background-default, #f7f7f7)", borderRadius: 2, p: "10px 14px", minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>{value}</Typography>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function ChartCard({ title, right, children, height = 200 }) {
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
    }}>
      {(title || right) && (
        <Box sx={{ display: "flex", alignItems: "baseline",
                   justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            {title}
          </Typography>
          {right && (
            <Typography sx={{ fontSize: 11, color: AVG_INK, whiteSpace: "nowrap" }}>
              {right}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ width: "100%", height: height === "auto" ? "auto" : height }}>
        {children}
      </Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => { if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); } });
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none", border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400, transition: "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>{opt}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

function GaugeCard({ label, value, subLabel, subValue, subColor = AMBER, pct = 0, gaugeColor = BLUE }) {
  const clampedPct = Math.min(100, Math.max(0, safeNum(pct)));
  const r = 52, cx = 70, cy = 66;
  const circ = Math.PI * r;
  const dash = (clampedPct / 100) * circ;
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.05em", mb: 1, alignSelf: "flex-start",
      }}>{label}</Typography>
      <svg width={140} height={76} style={{ overflow: "visible" }}>
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke="#e8e8e8" strokeWidth={12} strokeLinecap="round" />
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke={gaugeColor} strokeWidth={12} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`} />
        <text x={cx} y={cy - 6} textAnchor="middle" style={{ fontSize: 16, fontWeight: 500, fill: gaugeColor }}>
          {value}
        </text>
      </svg>
      {subLabel && (
        <>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>{subLabel}</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 500, color: subColor, mt: 0.5 }}>{subValue}</Typography>
        </>
      )}
    </Box>
  );
}

// ── IDs paste modal (nested) ───────────────────────────────────────
// Dedicated large paste box for one date's IDs — comfortably handles
// 700–1500. On Calculate it runs the per-date API, shows how many of
// the pasted IDs matched + the $ total, and writes count + amount back
// to the row. Stays open so you can review coverage before closing.
function IdsPasteModal({ open, date, initialIds, onClose, onApply }) {
  const [text,   setText]   = useState("");
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState("");
  const [result, setResult] = useState(null);   // { count, amount, requested, matched }

  // Reset ONLY when the modal opens. Depending on initialIds here would
  // re-fire after Calculate (the row's ids change → new prop reference) and
  // wipe the just-shown result — that was the "click twice" bug.
  React.useEffect(() => {
    if (open) { setText((initialIds || []).join("\n")); setBusy(false); setErr(""); setResult(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const ids = useMemo(() => parseIds(text), [text]);

  const run = async () => {
    if (!date)       { setErr("Set a date for this row first."); return; }
    if (!ids.length) { setErr("Paste at least one ID."); return; }
    setErr(""); setResult(null); setBusy(true);
    try {
      const res = await onApply(ids);   // parent runs the calc + updates the row, returns API result
      setResult(res || null);
    } catch (e) {
      setErr(`Calculate failed: ${e?.response?.data?.error || e?.message || "unknown"}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 500 }}>
        Paste IDs{date ? ` — ${fmtDate(date)}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>
          One per line, or separated by commas / spaces. Duplicates are removed automatically.
        </Typography>
        <TextField
          value={text}
          onChange={e => { setText(e.target.value); setResult(null); }}
          placeholder={"1001\n1002\n1003\n…"}
          multiline minRows={12} maxRows={20} fullWidth
          InputProps={{ sx: { fontSize: 12, fontFamily: "monospace", alignItems: "flex-start" } }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
          <Typography sx={{ fontSize: 12, color: ids.length ? BLUE : "text.secondary", fontWeight: 500 }}>
            {ids.length.toLocaleString()} unique ID{ids.length === 1 ? "" : "s"} parsed
          </Typography>
          <Box onClick={() => { setText(""); setResult(null); }} sx={{
            fontSize: 12, color: "text.secondary", cursor: "pointer", "&:hover": { color: "#A32D2D" },
          }}>Clear</Box>
        </Box>

        {result && (
          <Box sx={{
            mt: 1.5, p: "10px 12px", borderRadius: 2,
            background: "var(--mui-palette-background-default, #f7f7f7)",
            display: "flex", flexWrap: "wrap", gap: 2, alignItems: "baseline",
          }}>
            <Typography sx={{ fontSize: 13, color: "#3B6D11", fontWeight: 500 }}>
              ✓ Matched {Number(result.matched ?? result.count).toLocaleString()} of {Number(result.requested ?? ids.length).toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Count <b style={{ color: BLUE }}>{Number(result.count).toLocaleString()}</b>
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Total <b style={{ color: BLUE }}>{fmtCurrency(result.amount)}</b>
            </Typography>
          </Box>
        )}

        {result && (result.unmatched?.length > 0) && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography sx={{ fontSize: 12, color: "#A32D2D", fontWeight: 500 }}>
                {result.unmatched.length.toLocaleString()} unmatched ID{result.unmatched.length === 1 ? "" : "s"}
                {" "}(no record on {fmtDate(date)})
              </Typography>
              <Box
                onClick={() => { try { navigator.clipboard?.writeText(result.unmatched.join("\n")); } catch {} }}
                sx={{ fontSize: 12, color: BLUE, cursor: "pointer" }}
              >
                Copy
              </Box>
            </Box>
            <TextField
              value={result.unmatched.join("\n")}
              multiline minRows={3} maxRows={8} fullWidth
              InputProps={{ readOnly: true, sx: { fontSize: 12, fontFamily: "monospace", alignItems: "flex-start" } }}
            />
          </Box>
        )}

        {err && <Typography sx={{ fontSize: 12, color: "#A32D2D", mt: 0.5 }}>{err}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontSize: 13, color: "text.secondary" }}>
          {result ? "Done" : "Cancel"}
        </Button>
        <Button onClick={run} disabled={busy || !ids.length} variant="contained"
          sx={{ textTransform: "none", fontSize: 13, bgcolor: BLUE, minWidth: 96, "&:hover": { bgcolor: BLUE } }}>
          {busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : (result ? "Recalculate" : "Calculate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Edit modal ─────────────────────────────────────────────────────
// Two ways to populate the by-date table:
//   1. Manual   — type count + $ per date, add/remove rows
//   2. From IDs — each date row gets a "Paste IDs" button that opens
//                 the big paste box; Calculate fills that row's count + $
// Rows from either path are editable before Save.
function EditDataModal({ open, initialRows, onClose, onSave }) {
  const [mode,     setMode]     = useState("manual");  // 'manual' | 'ids'
  const [rows,     setRows]     = useState([]);        // {date, count, amount, ids} (values as strings)
  const [pasteIdx, setPasteIdx] = useState(null);      // row whose IDs we're pasting (null = closed)

  // Re-seed the draft whenever the modal is (re)opened.
  React.useEffect(() => {
    if (open) {
      setRows((initialRows || []).map(r => ({
        date: String(r.date).slice(0, 10),
        count: String(r.count ?? ""),
        amount: String(r.amount ?? ""),
        ids: r.ids || [],
      })));
      setMode("manual"); setPasteIdx(null);
    }
  }, [open, initialRows]);

  const updateRow = (i, field, val) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));
  const addRow    = () => setRows(rs => [...rs, { date: toLocal(new Date()), count: "", amount: "", ids: [] }]);

  // Run the per-date calc for one row's IDs, write count + amount back,
  // and return the API result so the paste modal can show match coverage.
  const applyIds = async (i, ids) => {
    const row = rows[i];
    const res = await calc24hrPayForDate({ date: row?.date, ids });
    // NOTE: count = matched customers (stays consistent with the $ total).
    // If you'd rather count the IDs you pasted regardless of match, use
    // res.requested here instead of res.count.
    setRows(rs => rs.map((r, idx) =>
      (idx === i ? { ...r, ids, count: String(res.count), amount: String(res.amount) } : r)));
    return res;
  };

  const save = () => {
    const clean = rows
      .filter(r => r.date)
      .map(r => ({
        date: String(r.date).slice(0, 10),
        count: safeNum(r.count), amount: safeNum(r.amount),
        ids: r.ids || [],
        source: (r.ids && r.ids.length) ? "ids" : "manual",
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Dates that existed when the modal opened but were removed → delete from DB.
    const keep    = new Set(clean.map(r => r.date));
    const removed = (initialRows || [])
      .map(r => String(r.date).slice(0, 10))
      .filter(d => !keep.has(d));

    onSave(clean, removed);
  };

  const inputSx = {
    "& .MuiInputBase-input": { fontSize: 12, py: "6px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
  };

  const idsMode  = mode === "ids";
  const gridCols = idsMode ? "1.2fr 1.1fr 0.8fr 0.9fr 28px" : "1.4fr 1fr 1fr 28px";

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 500 }}>Edit 24-hr payments</DialogTitle>
        <DialogContent dividers>
          {/* Mode toggle */}
          <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
            {[["manual", "Manual entry"], ["ids", "From IDs"]].map(([k, lbl]) => {
              const active = mode === k;
              return (
                <Box key={k} onClick={() => setMode(k)} sx={{
                  px: "12px", py: "4px", fontSize: 12, borderRadius: "20px",
                  cursor: "pointer", userSelect: "none", border: "0.5px solid",
                  borderColor: active ? BLUE : "divider",
                  background:  active ? BLUE : "transparent",
                  color:       active ? "#fff" : "text.secondary",
                  fontWeight:  active ? 500 : 400,
                }}>{lbl}</Box>
              );
            })}
          </Box>

          {idsMode && (
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>
              Add a row per date, then paste that date's IDs and Calculate to fill its count + amount.
            </Typography>
          )}

          {/* Header */}
          <Box sx={{ display: "grid", gridTemplateColumns: gridCols, gap: 1, alignItems: "center", mb: 0.5 }}>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Date</Typography>
            {idsMode && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>IDs</Typography>}
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Count</Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Amount ($)</Typography>
            <span />
          </Box>

          {/* Rows */}
          <Box sx={{ maxHeight: 320, overflow: "auto", display: "flex", flexDirection: "column", gap: 0.75 }}>
            {rows.map((r, i) => (
              <Box key={i} sx={{ display: "grid", gridTemplateColumns: gridCols, gap: 1, alignItems: "center" }}>
                <TextField type="date" value={r.date} onChange={e => updateRow(i, "date", e.target.value)}
                  size="small" sx={inputSx} />
                {idsMode && (
                  <Button onClick={() => setPasteIdx(i)} variant="outlined" size="small"
                    sx={{ textTransform: "none", fontSize: 12, borderRadius: "20px", whiteSpace: "nowrap",
                          borderColor: r.ids?.length ? BLUE : "divider",
                          color:       r.ids?.length ? BLUE : "text.secondary",
                          "&:hover": { borderColor: BLUE } }}>
                    {r.ids?.length ? `${r.ids.length.toLocaleString()} IDs` : "Paste IDs"}
                  </Button>
                )}
                <TextField type="number" value={r.count} onChange={e => updateRow(i, "count", e.target.value)}
                  size="small" sx={inputSx} inputProps={{ min: 0 }} />
                <TextField type="number" value={r.amount} onChange={e => updateRow(i, "amount", e.target.value)}
                  size="small" sx={inputSx} inputProps={{ min: 0, step: "0.01" }} />
                <Box onClick={() => removeRow(i)} sx={{
                  cursor: "pointer", color: "text.secondary", textAlign: "center",
                  fontSize: 16, lineHeight: 1, "&:hover": { color: "#A32D2D" },
                }}>✕</Box>
              </Box>
            ))}
            {!rows.length && (
              <Typography sx={{ fontSize: 12, color: "text.disabled", py: 2, textAlign: "center" }}>
                No rows yet — add a date to get started.
              </Typography>
            )}
          </Box>
          <Button onClick={addRow} sx={{ textTransform: "none", fontSize: 12, color: BLUE, mt: 1 }}>＋ Add date</Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: "text.secondary" }}>Cancel</Button>
          <Button onClick={save} variant="contained"
            sx={{ textTransform: "none", fontSize: 13, bgcolor: BLUE, "&:hover": { bgcolor: BLUE } }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nested per-date IDs paste box */}
      <IdsPasteModal
        open={pasteIdx !== null}
        date={pasteIdx !== null ? rows[pasteIdx]?.date : ""}
        initialIds={pasteIdx !== null ? rows[pasteIdx]?.ids : []}
        onClose={() => setPasteIdx(null)}
        onApply={(ids) => applyIds(pasteIdx, ids)}
      />
    </>
  );
}

// ── Funnel ─────────────────────────────────────────────────────────
// Left-aligned, tapering conversion funnel. `steps` is
//   [{ label, val, color, den?, amount? }]
//
// The GRAY background track is full width at every step; the colored
// fill inside it is the step's own percentage, so bar length and printed
// number always agree.
//
// Each step past the first carries its own denominator (`den`) for the
// in-bar percentage, so a step can be read against whatever base makes
// sense for it: RPC against Accounts, PTP and 24-hr Pay against RPC, Kept
// against PTP. The share of Sent drops to the small line under the label.
// Steps with no `den` (Accounts itself) keep % of Sent in the bar.
function Funnel({ steps }) {
  const base = (steps && steps[0] && steps[0].val) || 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5 }}>
      {(steps || []).map((s, i) => {
        const pctOfSent = base > 0 ? (s.val / base) * 100 : 0;
        const denPct    = s.den > 0 ? (s.val / s.den) * 100 : null;
        const isFirst   = i === 0;
        // The fill is the number printed inside the bar, 1:1 — no
        // magnification. A step with its own `den` fills to its share of
        // that base; the rest fill to their share of Sent. Floored at 2%
        // so a near-zero step still shows a sliver of color.
        const shownPct  = isFirst ? 100 : (denPct != null ? denPct : pctOfSent);
        const fillW     = shownPct > 0 ? Math.min(100, Math.max(shownPct, 2)) : 0;
        return (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
            {/* Left: fixed-width track holding the centered, resizing gray bar */}
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center" }}>
              <Box sx={{
                position: "relative", width: "100%", minWidth: 0,
                height: 46, background: "#f0f0f0", borderRadius: "4px",
                transition: "width .4s ease",
              }}>
                <Box sx={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${fillW}%`, background: s.color,
                  borderRadius: isFirst ? "4px" : "4px 0 0 4px",
                }} />
                {(
                  <Box sx={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 600, color: isFirst ? "#fff" : "#333",
                    pointerEvents: "none", whiteSpace: "nowrap",
                  }}>
                    {isFirst
                      ? Number(s.val).toLocaleString()
                      : denPct != null
                        ? `${denPct.toFixed(1)}%`
                        : `${pctOfSent.toFixed(1)}%`}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Right: label on line 1, then count + $ on line 2, % of Sent below */}
            <Box sx={{
              width: 90, flexShrink: 0, textAlign: "left",
              fontSize: 12, color: "text.secondary", lineHeight: 1.35,
            }}>
              <Box sx={{ color: s.color, fontWeight: 600 }}>{s.label}</Box>
              <Box sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
                {/* Count and $ on line 2; % of Sent drops to its own smaller line below. */}
                {!isFirst && <span style={{ fontWeight: 500 }}>{Number(s.val).toLocaleString()}</span>}
                {!isFirst && s.amount != null && (
                  <span> · <b style={{ color: s.color }}>{fmtCurrencyK1(s.amount)}</b></span>
                )}
              </Box>
              {denPct != null && (
                <Box sx={{ mt: 0.25, fontSize: 10, color: "text.disabled", whiteSpace: "nowrap" }}>
                  {pctOfSent.toFixed(1)}% of Sent
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Main dashboard ─────────────────────────────────────────────────
export default function TwentyFourHrPayDashboard() {
  const [gran,        setGran]        = useState("Week");
  const [rangePreset, setRangePreset] = useState("This Mo");
  const [editOpen,    setEditOpen]    = useState(false);
  const [loadErr,     setLoadErr]     = useState("");
  const [saveErr,     setSaveErr]     = useState("");

  // Saved 24-hr pay rows, loaded from the API.
  const [payData, setPayData] = useState([]);
  // PTP summary — only for the funnel's Sent / RPC / PTP / Kept steps.
  const [ptpSummary, setPtpSummary] = useState({});
  // Per-day Sent (texts_sent) for the 24-hr percentage graph.
  const [ptpByDay, setPtpByDay] = useState([]);
  // Accounts — distinct Accountid for the window, from its own endpoint.
  // NOTE: that route buckets in the CLIENT zone, while everything else on
  // this page (getPtpSummary / getPtpByDay) is UTC basis. The two agree
  // except for accounts whose only events fall inside the offset window at
  // a range edge, so Accounts ÷ Sent can be off by a hair on short ranges.
  const [accounts, setAccounts] = useState(0);
  // Funnel money steps.
  const [paySummary, setPaySummary] = useState([]);

  const loadData = useCallback(() => {
    setLoadErr("");
    get24hrPay()
      .then(rows => setPayData((rows || []).map(r => ({
        date:   String(r.pay_date).slice(0, 10),
        count:  safeNum(r.payment_count),
        amount: safeNum(r.payment_amount),
        ids:    [],   // list endpoint omits the ID blob; re-paste to recalc
      }))))
      .catch(e => setLoadErr(`Failed to load 24-hr pay data: ${e?.response?.data?.error || e?.message || "unknown"}`));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Persist the edited rows, delete any removed dates, then refresh from DB.
  const handleSave = useCallback(async (rows, removed) => {
    setSaveErr("");
    try {
      if (rows.length)            await save24hrPay(rows);
      if (removed && removed.length) await Promise.all(removed.map(d => delete24hrPay(d)));
      setPayData(rows);
      setEditOpen(false);
    } catch (e) {
      setSaveErr(`Failed to save: ${e?.response?.data?.error || e?.message || "unknown"}`);
    }
  }, []);

  const range     = useMemo(() => getDateRange(rangePreset), [rangePreset]);
  const prevRange = useMemo(() => getPrevDateRange(range.startDate, range.endDate), [range]);

  const curRows  = useMemo(() => rowsInRange(payData, range.startDate, range.endDate), [payData, range]);
  const prevRows = useMemo(() => rowsInRange(payData, prevRange.startDate, prevRange.endDate), [payData, prevRange]);

  const cur  = useMemo(() => aggregate(curRows),  [curRows]);
  const prev = useMemo(() => aggregate(prevRows), [prevRows]);

  const chartData = useMemo(() => bucketize(curRows, gran), [curRows, gran]);

  const cashOpts  = { goodWhenUp: true, fmtVal: v => fmtCurrency(v) };
  const countOpts = { goodWhenUp: true, fmtVal: v => v.toFixed(1) };

  const kpis = useMemo(() => ({
    ...cur,
    totalCountDelta:  buildDelta(cur.totalCount,     prev.totalCount,     { goodWhenUp: true }),
    totalAmtDelta:    buildDelta(cur.totalAmt,       prev.totalAmt,       cashOpts),
    avgAmtDelta:      buildDelta(cur.avgAmt,         prev.avgAmt,         cashOpts),
    avgCountDelta:    buildDelta(cur.avgCountPerDay, prev.avgCountPerDay, countOpts),
    medianDelta:      buildDelta(cur.medianDailyAvg, prev.medianDailyAvg, cashOpts),
    modeDelta:        buildDelta(cur.modeDailyAvg,   prev.modeDailyAvg,   cashOpts),
  }), [cur, prev]); // eslint-disable-line react-hooks/exhaustive-deps

  // PTP summary for the funnel (channel defaults to texts server-side).
  useEffect(() => {
    getPtpSummary({ startDate: range.startDate, endDate: range.endDate })
      .then(d => setPtpSummary(d || {}))
      .catch(() => setPtpSummary({}));
  }, [range]);

  // PTP / Kept / money for the funnel, from the payments route.
  useEffect(() => {
    getPaymentsSummary({ startDate: range.startDate, endDate: range.endDate })
      .then(d => setPaySummary(d || []))
      .catch(() => setPaySummary([]));
  }, [range]);

  // Accounts for the funnel's dedupe step.
  useEffect(() => {
    getAccounts({ startDate: range.startDate, endDate: range.endDate })
      .then(d => setAccounts(safeNum(d?.account_count)))
      .catch(() => setAccounts(0));
  }, [range]);

  // Per-day Sent for the 24-hr percentage graph. Requested at DAY
  // granularity regardless of the page's `gran` slicer — we re-bucket the
  // daily Sent client-side in pctData so it always aligns to the 24-hr
  // pay buckets. ptp-by-day returns one row per (date, dpd_bucket), so we
  // sum texts_sent across buckets to get each day's Sent.
  useEffect(() => {
    getPtpByDay({ startDate: range.startDate, endDate: range.endDate, granularity: "day" })
      .then(d => setPtpByDay(d || []))
      .catch(() => setPtpByDay([]));
  }, [range]);

  // date(YYYY-MM-DD) → Sent for that day.
  const sentByDate = useMemo(() => {
    const m = new Map();
    (ptpByDay || []).forEach(r => {
      const d = String(r.summary_date).slice(0, 10);
      m.set(d, (m.get(d) || 0) + safeNum(r.texts_sent));
    });
    return m;
  }, [ptpByDay]);

  // 24-hr % per bucket = (24-hr pay count) / (Sent) for that bucket.
  // Only dates that actually have 24-hr pay rows are included — we iterate
  // curRows, so days without 24-hr IDs never appear even if they had sends.
  // Bucketing sums pay + sent first, then divides (not an average of %s).
  const pctData = useMemo(() => {
    const map = new Map();
    for (const r of curRows) {
      const d = String(r.date).slice(0, 10);
      const k = bucketKey(d, gran);
      const e = map.get(k) || { pay: 0, sent: 0 };
      e.pay  += safeNum(r.count);
      e.sent += sentByDate.get(d) || 0;
      map.set(k, e);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, v]) => ({
        date: bucketLabel(k, gran),
        pct:  v.sent > 0 ? (v.pay / v.sent) * 100 : 0,
        pay:  v.pay,
        sent: v.sent,
      }));
  }, [curRows, sentByDate, gran]);

  const pctKpis = useMemo(() => {
    const pcts      = pctData.map(d => d.pct).filter(v => v > 0);
    const totalPay  = pctData.reduce((a, b) => a + b.pay,  0);
    const totalSent = pctData.reduce((a, b) => a + b.sent, 0);
    return {
      overall: totalSent > 0 ? (totalPay / totalSent) * 100 : 0,
      avg: pcts.length ? pcts.reduce((a, b) => a + b, 0) / pcts.length : 0,
      max: pcts.length ? Math.max(...pcts) : 0,
      min: pcts.length ? Math.min(...pcts) : 0,
    };
  }, [pctData]);

  // Conversion funnel: Sent → RPC → PTP → Kept → 24-hr Pay.
  // Sent / RPC / PTP / Kept come from the PTP summary; the 24-hr Pay step
  // is this page's own count + $ for the range. Labels are display-only
  // renames: "RPC" is rpc_count, "Kept" is processed_ptp_count. RPC is read
  // against Accounts; PTP and 24-hr Pay against RPC; Kept against PTP.
  const funnelSteps = useMemo(() => {
    const rpc       = safeNum(ptpSummary.rpc_count);
    const pay       = paymentsTotals(paySummary);
    // PTP = Kept + promises; Kept is a share of a base that contains it.
    // Both come from the payments route, with ptp-summary as the fallback.
    const kept      = pay.has ? pay.kept : safeNum(ptpSummary.processed_ptp_count);
    const committed = pay.has ? pay.ptp
                              : safeNum(ptpSummary.processed_ptp_count) + safeNum(ptpSummary.promise_count);
    const keptAmt   = pay.has ? pay.keptAmt : safeNum(ptpSummary.processed_ptp_amount);
    const ptpAmount = pay.has ? pay.ptpAmt
                              : safeNum(ptpSummary.processed_ptp_amount) + safeNum(ptpSummary.promise_amount);
    return [
      { label: "Sent",      val: safeNum(ptpSummary.texts_sent),          color: C_SENT },
      { label: "Accounts",  val: accounts,                                color: C_ACCOUNTS },
      { label: "RPC",       val: rpc,                                     color: C_RPC,     den: accounts },
      { label: "PTP",       val: committed,                               color: C_PTP,     den: rpc,       amount: ptpAmount },
      { label: "Same Day",      val: kept,                                    color: C_KEPT,    den: committed, amount: keptAmt },
      { label: "24-hr Pay", val: cur.totalCount,                          color: C_24HR,    den: rpc,       amount: cur.totalAmt },
    ];
  }, [ptpSummary, paySummary, accounts, cur.totalCount, cur.totalAmt]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };
  // ── THE RANGE THE PILLS RESOLVED TO ──
  // Appended to every chart title and the funnel, the way the scorecard does
  // it. The pills say "90D"; only this says which 90 days, and a screenshot
  // or a PDF export carries no pill state at all.
  const rangeLabel = `${fmtDate(range.startDate)} – ${fmtDate(range.endDate)}`;
  const titleOf    = (t) => (rangeLabel ? `${t} · ${rangeLabel}` : t);

  // Averages behind the dotted rules. The 24-hr rate is POOLED — total pay
  // over total sent — which is pctKpis.overall, not the mean of the per-day
  // percentages sitting beside it in that stat strip.
  const avgAmount  = activeMean(chartData, "amount");
  const avgCount   = activeMean(chartData, "count");
  const avgPct     = pooledPct(pctData, "pay", "sent");



  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup label="Date range" options={RANGE_OPTIONS} value={rangePreset} onChange={setRangePreset} groups={[3, 4, 2, 4]} />
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={() => setEditOpen(true)} variant="outlined" size="small"
          sx={{ textTransform: "none", fontSize: 12, borderRadius: "20px", borderColor: "divider",
                color: BLUE, alignSelf: "flex-end", "&:hover": { borderColor: BLUE } }}>
          ✎ Edit data
        </Button>
      </Box>

      {loadErr && <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>}
      {saveErr && <Alert severity="error" sx={{ mb: 2 }}>{saveErr}</Alert>}

      {/* ── AMOUNT OVER TIME ── */}
      {/* The page's KPI row: Total / Avg / Max / Min. Counts and rates are in
          the funnel, so they are not repeated here — this row is only about
          the shape of the amounts. */}
      <SectionTitle>24-hr payments</SectionTitle>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
        <MiniStat label="Total"    value={fmtCurrency(kpis.totalAmt)}   color={C_24HR} />
        <MiniStat label="Avg"      value={fmtCurrency(kpis.avgAmt)}     color={C_24HR} />
        <MiniStat label="Max day"  value={fmtCurrency(kpis.maxDayAmt)}  color={C_24HR} />
        <MiniStat label="Min day"  value={fmtCurrency(kpis.minDayAmt)}  color={C_24HR} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 1.5, mb: 0.5 }}>
        <ChartCard title={titleOf("24-hr Pay $")} right={`avg ${fmtCurrencyG(avgAmount)}`} height={300}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="amtGrad24" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C_24HR} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={C_24HR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis dataKey="date" tick={axisStyle} />
              <YAxis tick={axisStyle} tickFormatter={v => fmtCurrencyG(v)} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtCurrencyG(v), n]} />
              <ReferenceLine y={avgAmount} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
              <Area type="monotone" dataKey="amount" name="Amount"
                stroke={C_24HR} fill="url(#amtGrad24)" strokeWidth={2} dot={{ r: 3 }}>
                <LabelList dataKey="amount" position="top" offset={8}
                  fontSize={10} fill={C_24HR} formatter={v => fmtCurrencyG(v)} />
              </Area>
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={titleOf("Conversion funnel")} height="auto">
          <Funnel steps={funnelSteps} />
        </ChartCard>
      </Box>

      {/* ── Methodology note ── */}
      <Box sx={{
        mb: 1.5, p: "10px 14px", borderRadius: 2,
        border: "0.5px solid", borderColor: `${AMBER}55`,
        background: `${AMBER}0F`, fontSize: 12, color: "text.secondary", lineHeight: 1.5,
      }}>
        <b style={{ color: AMBER }}>Note:</b>{" "}
        <b>
          The graphs and conversion figures on this page are derived from the account IDs that
          made a payment, mapped to the actual amount paid on each account.
        </b>{" "}
        Partial payments are now taken into account — an account that paid only part of what it
        owed is counted at the amount it actually paid, so the totals and conversion rates
        reflect the real payments received. The <b>24-hr %</b> below divides each day's 24-hr payment
        count by that day's texts Sent; because Sent is texts-only and the payment count is
        all-channel, the percentage can run slightly high if any IDs came from email.
      </Box>

      {/* ── PERCENTAGE OVER TIME ── 24-hr pay count ÷ Sent, per day.
          Only days that have 24-hr IDs/amounts are shown (driven by curRows). ── */}
      <SectionTitle>24-hr percentage — count ÷ sent over time</SectionTitle>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1 }}>
        <MiniStat label="Overall %" value={fmtPct(pctKpis.overall)} color={AMBER} />
        <MiniStat label="Avg day %" value={fmtPct(pctKpis.avg)}     color={AMBER} />
        <MiniStat label="Max day %" value={fmtPct(pctKpis.max)}     color={AMBER} />
        <MiniStat label="Min day %" value={fmtPct(pctKpis.min)}     color={AMBER} />
      </Box>
      <ChartCard title={titleOf("24-hr %")} right={`avg ${fmtPct(avgPct)}`} height={220}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={pctData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="pctGrad24" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={AMBER} stopOpacity={0.15} />
                <stop offset="95%" stopColor={AMBER} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
            <XAxis dataKey="date" tick={axisStyle} />
            <YAxis tick={axisStyle} tickFormatter={v => `${v}%`} domain={[0, "auto"]} />
            <Tooltip contentStyle={tooltipStyle}
              formatter={(v, n, p) => {
                if (n === "24-hr %") {
                  const d = p?.payload || {};
                  return [`${Number(v).toFixed(2)}%  (${Number(d.pay).toLocaleString()} / ${Number(d.sent).toLocaleString()})`, n];
                }
                return [v, n];
              }} />
            <ReferenceLine y={avgPct} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
            <Area type="monotone" dataKey="pct" name="24-hr %"
              stroke={AMBER} fill="url(#pctGrad24)" strokeWidth={2} dot={{ r: 3 }}>
              <LabelList dataKey="pct" position="top" offset={8}
                fontSize={10} fill={AMBER} formatter={v => `${Number(v).toFixed(1)}%`} />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ── COUNT OVER TIME ── */}
      <SectionTitle>24-hr payments — count</SectionTitle>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 1, mb: 1 }}>
        <MiniStat label="Total"    value={kpis.totalCount.toLocaleString()} color={C_24HR} />
        <MiniStat label="Avg/day"  value={kpis.avgCountPerDay.toFixed(1)}   color={C_24HR} />
        <MiniStat label="Max day"  value={kpis.maxDayCount.toLocaleString()} color={C_24HR} />
        <MiniStat label="Min day"  value={kpis.minDayCount.toLocaleString()} color={C_24HR} />
      </Box>
      <ChartCard title={titleOf("24-hr Pay count")} right={`avg ${avgCount.toFixed(1)}`} height={200}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="cntGrad24" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={C_24HR} stopOpacity={0.15} />
                <stop offset="95%" stopColor={C_24HR} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
            <XAxis dataKey="date" tick={axisStyle} />
            <YAxis tick={axisStyle} />
            <Tooltip contentStyle={tooltipStyle} />
            <ReferenceLine y={avgCount} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
            <Area type="monotone" dataKey="count" name="Count"
              stroke={C_24HR} fill="url(#cntGrad24)" strokeWidth={2} dot={{ r: 3 }}>
              <LabelList dataKey="count" position="top" offset={8} fontSize={10} fill={C_24HR} />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ── OVERVIEW ── count (bars) vs amount (line) ── */}
      <SectionTitle>Overview</SectionTitle>
      <ChartCard title={titleOf("Count vs amount")} height={220}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
            <XAxis dataKey="date" tick={axisStyle} />
            <YAxis yAxisId="left"  tick={axisStyle} />
            <YAxis yAxisId="right" orientation="right" tick={axisStyle} tickFormatter={v => fmtCurrencyG(v)} />
            <Tooltip contentStyle={tooltipStyle}
              formatter={(v, n) => (n === "Amount" ? [fmtCurrencyG(v), n] : [v, n])} />
            <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
            <Bar  yAxisId="left"  dataKey="count"  name="Count"  fill={C_24HR} radius={[3, 3, 0, 0]} />
            <Line yAxisId="right" dataKey="amount" name="Amount" stroke={C_24HR} strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <EditDataModal
        open={editOpen}
        initialRows={payData}
        onClose={() => setEditOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
}